import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@agms/ui'
import { helpApi, ticketApi } from '../../lib/api'
import type { Ticket, TicketArticleLink } from '../../lib/api'
import { apiErrorMessage } from '../../hooks/useErrorHandler'

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [value, delay])

  return debounced
}

export function TicketHelpArticles({
  ticketId,
  links,
  canEdit,
  onUpdated,
}: {
  ticketId: string
  links: TicketArticleLink[]
  canEdit: boolean
  onUpdated: (ticket: Ticket) => void
}) {
  const [query, setQuery] = useState('')
  const [solved, setSolved] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounced = useDebounced(query.trim(), 200)
  const linkedIds = new Set(links.map((link) => link.article_id))

  const search = useQuery({
    queryKey: ['ticket-help-search', ticketId, debounced],
    queryFn: () => helpApi.listArticles({ search: debounced, per_page: 8 }),
    enabled: canEdit && debounced.length >= 2,
  })

  const finish = (updated: Ticket) => {
    setError(null)
    setQuery('')
    onUpdated(updated)
  }

  const linkMutation = useMutation({
    mutationFn: (articleId: string) => ticketApi.linkArticle(ticketId, { article_id: articleId, solved }),
    onSuccess: finish,
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not link that article.'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: (payload: { articleId: string; solved: boolean }) => (
      ticketApi.updateArticleLink(ticketId, payload.articleId, { solved: payload.solved })
    ),
    onSuccess: (updated) => {
      setError(null)
      onUpdated(updated)
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not update that article link.'))
    },
  })

  const unlinkMutation = useMutation({
    mutationFn: (articleId: string) => ticketApi.unlinkArticle(ticketId, articleId),
    onSuccess: (updated) => {
      setError(null)
      onUpdated(updated)
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not unlink that article.'))
    },
  })

  const matches = (search.data?.data ?? []).filter((article) => !linkedIds.has(article.id))
  const busy = linkMutation.isPending || updateMutation.isPending || unlinkMutation.isPending

  return (
    <Card>
      <CardHeader><CardTitle id="ticket-help-articles-heading">Help articles</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-4" aria-labelledby="ticket-help-articles-heading">
          {links.length === 0 ? (
            <p className="text-sm text-gray-500">No Help articles linked yet.</p>
          ) : (
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link.id} className="rounded-md border border-gray-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/help/${link.slug}`} className="text-sm font-semibold text-blue-700 hover:text-blue-900">
                        {link.title}
                      </Link>
                      <p className="mt-1 text-xs text-gray-500">
                        <span>{link.solved ? 'Solved this ticket' : 'Related'}</span>
                        <span>
                          {' · '}
                          {link.linked_by?.name ? `Linked by ${link.linked_by.name}` : 'Linked by a teammate'}
                        </span>
                      </p>
                    </div>
                    {canEdit ? (
                      <Button
                        variant="secondary"
                        aria-label={`Unlink ${link.title}`}
                        onClick={() => unlinkMutation.mutate(link.article_id)}
                        disabled={busy}
                      >
                        Unlink
                      </Button>
                    ) : null}
                  </div>
                  {canEdit ? (
                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={link.solved}
                        disabled={busy}
                        aria-label={`This article solved it: ${link.title}`}
                        onChange={(event) => updateMutation.mutate({
                          articleId: link.article_id,
                          solved: event.target.checked,
                        })}
                      />
                      This article solved it
                    </label>
                  ) : null}
                </li>
              ))}
            </ul>
          )}

          {canEdit ? (
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <label className="block text-sm font-medium text-gray-700" htmlFor="ticket-help-search">
                Search Help articles
                <input
                  id="ticket-help-search"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by title"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={solved}
                  onChange={(event) => setSolved(event.target.checked)}
                />
                This article solved it
              </label>
              {debounced.length >= 2 && search.isFetching ? (
                <p className="text-sm text-gray-500">Searching...</p>
              ) : null}
              {debounced.length >= 2 && search.isSuccess && matches.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {(search.data?.data.length ?? 0) > 0 ? 'Those articles are already linked.' : 'No matching articles.'}
                </p>
              ) : null}
              {matches.length > 0 ? (
                <ul className="space-y-2">
                  {matches.map((article) => (
                    <li key={article.id} className="flex items-center justify-between gap-3">
                      <span className="min-w-0 truncate text-sm text-gray-800">{article.title}</span>
                      <Button
                        onClick={() => linkMutation.mutate(article.id)}
                        disabled={busy}
                      >
                        Link {article.title}
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-rose-700">{error}</p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
